/* eslint-disable @typescript-eslint/no-explicit-any */
import {taggingClickBanner, setTaggingData} from "@COMPONENTS/common/tagging/taggingCommon";
import {
	taggingClickCouponDown,
	taggingClickEventListPopup,
	taggingClickEventReserve,
	taggingClickEventWish,
	taggingClickShareEvent,
	taggingLoadDetailPackage,
} from "@COMPONENTS/common/tagging/taggingDetailPackage";
import {taggingClickGNB, taggingClickGNBIcon, taggingClickGNBSearch} from "@COMPONENTS/common/tagging/taggingGNB";
import {
	taggingClickGoods,
	taggingClickShareGoods,
	taggingLoadCompareGoods,
	taggingLoadLocalList,
} from "@COMPONENTS/common/tagging/taggingLocalList";
import {taggingClickReserve, taggingLoadReserveComplete} from "@COMPONENTS/common/tagging/taggingReserve";
import {CouponPopType} from "@COMPONENTS/product/common/popup/desktop/CouponPopup";
import logger from "@FRAMEWORK/utils/logger";
import {amplitude, braze} from "@kit/utils";
import {GlobarBannerText} from "@QUERIES/bff/global/useGlobalBannerText";
import {GlobalMenu} from "@QUERIES/bff/global/useGlobalMenu";
import {GlobarOpt} from "@QUERIES/bff/global/useGlobalOpt";
import {BannerContents} from "@QUERIES/essential/banner/useBannerContents";
import {CouponByEvent} from "@QUERIES/prdt/coupon/useCouponByEvent";
import {EventDetail} from "@QUERIES/prdt/event/useEventDetail";
import {GoodsCompare} from "@QUERIES/prdt/goods/useGoodsCompare";
import {ReserveDetail} from "@QUERIES/prdt/reserve/useReserveDetail";
import {QueryClient, QueryKey, useQueryClient} from "@tanstack/react-query";
import {useEffect} from "react";

/** 사용할 queryKey */
const taggingQueryKeys = [
	"useGlobalMenuBFF",
	"useGoodsFilter",
	"useGoodsByMenu",
	"useEventPrice",
	"useEventMileage",
	"useSigninUser",
] as const;
export type TaggingQueryKey = (typeof taggingQueryKeys)[number];

type TaggingClickMap = {
	// common
	pkg_select_promo_calender: BannerContents & {selectDiv: string};
	pkg_select_promo_locallist: BannerContents & {selectDiv: string};
	pkg_select_promo_dtl: BannerContents & {selectDiv: string};
	// localList
	pkg_select_prod_list: {goodsCd: string};
	pkg_share_goodsCd: {parameter: string; shareCh: string};
	// detailPackage
	pkg_share_dtl: EventDetail & {shareCh: string};
	pkg_select_dtl_change: EventDetail;
	pkg_select_evCd_wish: EventDetail & {wishYn: string};
	pkg_select_evCd_pkgDtl: {eventDetail: EventDetail} & {
		count: {adultCnt: number; childCnt: number; infantCnt: number};
	} & {
		coupon: CouponPopType[];
	};
	goods_down_coupon: CouponByEvent & {evCd: string; evNm: string; selectDiv: string};
	// reserve
	pkg_select_evCd_reserve: {eventDetail: EventDetail} & {
		count: {adultCnt: number; childCnt: number; infantCnt: number};
	} & {
		coupon: CouponPopType[];
	};
	// GNB
	gnb_click: GlobalMenu;
	pkg_select_icon_main: GlobarOpt;
	pkg_search_keyword: GlobarBannerText;
};
export type TaggingClickName = keyof TaggingClickMap;
export type TaggingClickData<T extends TaggingClickName> = TaggingClickMap[T];

type TaggingLoadMap = {
	// localList
	pkg_view_goodsCdList: null;
	pkg_compare_goodsCd: GoodsCompare[];
	// detailPackage
	pkg_view_dtl: EventDetail;
	// reserveCompleute
	pkg_booking_complete: {eventDetail: EventDetail} & {reserveDetail: ReserveDetail};
};
export type TaggingLoadName = keyof TaggingLoadMap;
export type TaggingLoadData<T extends TaggingLoadName> = TaggingLoadMap[T];

const common = () => ({cat: "PKG"});

const brazeEvent = (eventName: string, data?: any) => {
	data && braze.logCustomEvent(eventName, {...common(), ...data});
};

const brazeAttribute = (eventName: string, data?: any) => {
	data && braze.addToCustomAttributeArray(eventName, {...common(), ...data});
};

const brazePurChase = (data?: any) => {
	if (!data || !data.productId || !data.price || !data.quantity) return;
	const {productId, price, currencyCode, quantity, purchaseProperties = {}} = data;
	braze.logPurchase(productId, price, currencyCode, quantity, purchaseProperties);
};

const amplitudeTrack = (eventName: string, data?: any) => {
	data && amplitude.track(eventName, {...common(), ...data});
};

const amplitudeRevenue = (data?: any) => {
	if (!data || !data.productId || !data.price || !data.quantity || !data.revenue) return;
	const {productId, price, quantity, revenue} = data;
	amplitude
		.Revenue()
		.setProductId(productId)
		.setPrice(price)
		.setQuantity(quantity)
		.setRevenueType("pkg")
		.setRevenue(revenue)
		.send();
};

type Tracking = "taggingName" | "event" | "track" | "attribute" | "purchase" | "revenue";
const tracking = (props: {taggingName; event?; track?; attribute?; purchase?; revenue?}) => {
	const {taggingName, event, track, attribute, purchase, revenue} = props;
	event && brazeEvent(taggingName, event);
	track && amplitudeTrack(taggingName, track);
	attribute && brazeAttribute(taggingName, attribute);
	purchase && brazePurChase(purchase);
	revenue && amplitudeRevenue(revenue);
};

/**
 * @param queryClient
 * @returns boolean
 * @description taggingQueryKeys에 포함하는 queryClient 패치여부
 * @description window 객체에 세팅된 값과 비교하여 패치된 경우 window 객체에 세팅
 */
const isQueryFetched = (queryClient: QueryClient) => {
	// taggingQueryKeys 포함된 것만 가져오기
	const filteredQueries: [QueryKey, any][] = queryClient
		.getQueriesData({})
		.filter(([queryKey]) => Array.isArray(queryKey) && taggingQueryKeys.includes(queryKey[0] as TaggingQueryKey));
	const filteredQueryKeys = filteredQueries.map(item => item[0][0]);

	// window 객체에 세팅 됬는지 확인
	const windowKeys = Object.keys(window.tagging);
	const isWindowSet = filteredQueryKeys.toSorted().join() === windowKeys.toSorted().join();
	if (isWindowSet) {
		return isWindowSet;
	}

	// queries 패치 여부
	const isQueriesFetched = filteredQueries.every(([, data]) => data);
	if (isQueriesFetched) {
		// window 객체에 세팅
		filteredQueries.forEach(([queryKey, data]) => setTaggingData(queryKey[0] as TaggingQueryKey, data.body));
		return isQueriesFetched;
	}
	return false;
};

export const useTaggingLoad = <T extends TaggingLoadName>(taggingName: T, taggingData?: TaggingLoadData<T>) => {
	try {
		if (!taggingName) return;
		const taggingLoad = () => {
			const handlers: Record<TaggingLoadName, (props?: any) => Partial<Record<Tracking, any>>> = {
				// localList.page
				pkg_view_goodsCdList: () => {
					const data = taggingLoadLocalList();
					return {event: data, track: data};
				},
				pkg_compare_goodsCd: props => {
					const data = taggingLoadCompareGoods(props);
					return {event: data, track: data};
				},
				// detailpackage.page
				pkg_view_dtl: props => {
					const {data, attribute} = taggingLoadDetailPackage(props);
					return {event: data, track: data, attribute};
				},
				// reserveComplete.page
				pkg_booking_complete: props => {
					const {data, purchase, revenue} = taggingLoadReserveComplete(props);
					return {event: data, track: data, purchase, revenue};
				},
			};

			if (handlers[taggingName]) {
				const result = handlers[taggingName](taggingData);
				tracking({taggingName, ...result});
			}
		};

		const queryClient = useQueryClient();
		useEffect(() => {
			const intervalId = setInterval(() => {
				const isQuery = isQueryFetched(queryClient);
				if (isQuery) {
					taggingLoad();
					clearInterval(intervalId);
				}
			}, 1000);
			return () => {
				if (intervalId) clearInterval(intervalId);
			};
		}, [taggingData]);
	} catch (error) {
		logger.error({message: "useTaggingLoad"});
	}
};

export const useTaggingClick = <T extends TaggingClickName>(
	taggingName: T,
	taggingData?: Partial<TaggingClickData<T>>,
) => {
	try {
		if (!taggingName) return;
		const taggingClick = (addData?: Partial<TaggingClickData<T>>) => {
			const mergedData = {...taggingData, ...addData} as any;
			const handlers: Record<TaggingClickName, (props?: any) => Partial<Record<Tracking, any>>> = {
				// GNB
				gnb_click: () => ({track: taggingClickGNB(mergedData)}),
				pkg_select_icon_main: () => ({track: taggingClickGNBIcon(mergedData)}),
				pkg_search_keyword: () => ({track: taggingClickGNBSearch(mergedData)}),
				// banner
				pkg_select_promo_calender: () => ({track: taggingClickBanner(mergedData)}),
				pkg_select_promo_locallist: () => ({track: taggingClickBanner(mergedData)}),
				pkg_select_promo_dtl: () => ({track: taggingClickBanner(mergedData)}),
				// localList.page
				pkg_select_prod_list: () => {
					const {data, attribute} = taggingClickGoods(mergedData);
					return {event: data, track: data, attribute};
				},
				pkg_share_goodsCd: () => {
					const data = taggingClickShareGoods(mergedData);
					return {event: data, track: data};
				},
				// detailPackage.page
				pkg_select_dtl_change: () => {
					const data = taggingClickEventListPopup(mergedData);
					return {event: data, track: data};
				},
				goods_down_coupon: () => {
					const data = taggingClickCouponDown(mergedData);
					return {event: data, track: data};
				},
				pkg_share_dtl: () => {
					const data = taggingClickShareEvent(mergedData);
					return {event: data, track: data};
				},
				pkg_select_evCd_wish: () => {
					const data = taggingClickEventWish(mergedData);
					return {event: data, track: data};
				},
				pkg_select_evCd_pkgDtl: () => {
					const data = taggingClickEventReserve(mergedData);
					return {event: data, track: data};
				},
				// reserve.page
				pkg_select_evCd_reserve: () => ({track: taggingClickReserve(mergedData)}),
			};
			if (handlers[taggingName]) {
				const result = handlers[taggingName](taggingData);
				tracking({taggingName, ...result});
			}
		};

		const queryClient = useQueryClient();
		useEffect(() => {
			const intervalId = setInterval(() => {
				const isQuery = isQueryFetched(queryClient);
				if (isQuery) clearInterval(intervalId);
			}, 1000);
		}, []);

		return {taggingClick};
	} catch (error) {
		logger.error({message: "useTaggingClick"});
		return {
			taggingClick: () => null,
		};
	}
};
